import React, { useContext, useEffect, useState } from 'react'
import API from '../../services/API'
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  LinearProgress,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import moment from 'moment'
import AssignedTo from './AssignedTo'
import AnDateField from '../DateTime/DateField'
import Comments from '../Comments/Comments'
import { GlobalContext, UserContext } from '../../App'
import TipTap from '../TipTap/TipTap'
import {
  PRIORITIES,
  PRIORITY_META,
  STATUSES,
  STATUS_META,
  getDueMeta,
  getTeamColorKey,
  normaliseTask,
  resolveColor,
} from '../../pages/userPages/tasks/taskHelpers'

const FieldLabel = ({ children }) => (
  <Typography
    variant="caption"
    sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5, display: 'block' }}
  >
    {children}
  </Typography>
)

const Dot = ({ color }) => (
  <Box
    sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }}
  />
)

// Linked records the detail endpoint populates, in display order
const LINKS = [
  { key: 'event', type: 'Event', label: (x) => x.title, url: (x) => `/events/${x._id}` },
  { key: 'incident', type: 'Incident', label: (x) => x.incidentNumber, url: (x) => `/incidents/${x._id}` },
  { key: 'vehicle', type: 'Vehicle', label: (x) => x.callSign, url: (x) => `/fleet/${x._id}` },
  { key: 'defect', type: 'Defect', label: (x) => x.summary, url: (x) => `/defects/${x._id}` },
  { key: 'safetyAlert', type: 'Safety alert', label: (x) => x.title, url: (x) => `/safety-alerts/${x._id}` },
]

// Task detail, rendered inside a right-hand Drawer by MyTasks and the event
// tasks pane. `onComplete` fires after a successful save so the caller can
// refresh its list; `onClose` (optional) renders a close button.
const TaskPane = ({ taskID, onComplete, onClose }) => {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [fetchError, setFetchError] = useState(false)
  const [task, setTask] = useState({})
  const [taskDescription, setTaskDescription] = useState('')

  const { user } = useContext(UserContext)
  const { setToast } = useContext(GlobalContext)
  const theme = useTheme()

  const loadTask = () => {
    if (!taskID) return
    setLoading(true)
    setFetchError(false)
    API.get('/task/' + taskID)
      .then((res) => {
        if (res.status === 200 && res.data) {
          setTask(normaliseTask(res.data))
          setTaskDescription(res.data.taskDescription ?? '')
        } else {
          setFetchError(true)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => loadTask(), [taskID])

  const save = (overrides = {}) => {
    const data = { ...task, ...overrides, taskDescription }
    delete data.comments
    delete data.createdBy
    delete data.links
    setSaving(true)
    return API.put('/task/' + taskID, data)
      .then((res) => {
        if (res.status === 200) {
          setToast(true)
          onComplete && onComplete()
        }
      })
      .finally(() => setSaving(false))
  }

  const update = (patch) => setTask((prev) => ({ ...prev, ...patch }))

  const due = task.dueDate ? getDueMeta(task) : null
  const linkedRecords = LINKS.filter((link) => task?.links?.[link.key])
  const completed = task.taskStatus === 'Completed'
  const assignedToMe =
    !!task.assignedToUser && String(task.assignedToUser._id) === String(user._id)

  return (
    <Box sx={{ width: { xs: '100vw', sm: 600 }, maxWidth: '100vw' }}>
      {loading ? (
        <LinearProgress />
      ) : fetchError ? (
        <Typography sx={{ p: 3 }} color="error">
          Unable to load this task.
        </Typography>
      ) : (
        <Stack>
          <Stack
            direction="row"
            alignItems="center"
            gap={1.5}
            sx={{
              height: 56,
              px: 2,
              pl: 2.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'text.secondary',
              }}
            >
              Task
            </Typography>
            {task.assignedToTeam && (
              <Chip
                size="small"
                variant="outlined"
                icon={
                  <Dot
                    color={resolveColor(
                      theme,
                      getTeamColorKey(task.assignedToTeam),
                    )}
                  />
                }
                label={`${task.assignedToTeam} team`}
                sx={{ borderRadius: 1, pl: 0.75, height: 22, fontSize: 12 }}
              />
            )}
            <Box flex={1} />
            <Typography variant="caption" color="text.disabled">
              Created {moment(task.createdAt).format('D MMM YYYY, HH:mm')}
            </Typography>
            {onClose && (
              <IconButton size="small" onClick={onClose} aria-label="Close">
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>

          <Stack gap={2.5} sx={{ p: 2.5 }}>
            <Box>
              <TextField
                value={task.taskName ?? ''}
                onChange={(e) => update({ taskName: e.target.value })}
                fullWidth
                variant="standard"
                placeholder="Task name"
                InputProps={{
                  disableUnderline: true,
                  sx: { fontSize: '1.2rem', fontWeight: 600, lineHeight: 1.35 },
                }}
              />
              {due && due.bucket === 'overdue' && (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    color: 'error.main',
                    fontWeight: 500,
                    mt: 0.5,
                  }}
                >
                  <ErrorOutlineIcon sx={{ fontSize: 16 }} />
                  Overdue by {due.daysOverdue} day{due.daysOverdue === 1 ? '' : 's'}
                </Typography>
              )}
            </Box>

            <Box>
              <FieldLabel>Status</FieldLabel>
              <Stack
                direction="row"
                sx={{
                  border: '1px solid',
                  borderColor: '#CBD5E1',
                  borderRadius: 1,
                  overflow: 'hidden',
                  height: 36,
                }}
              >
                {STATUSES.map((status, index) => {
                  const selected = task.taskStatus === status
                  const color = resolveColor(theme, STATUS_META[status].colorKey)
                  return (
                    <Box
                      key={status}
                      onClick={() => update({ taskStatus: status })}
                      sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.75,
                        fontSize: '0.85rem',
                        fontWeight: 500,
                        cursor: 'pointer',
                        borderRight: index < STATUSES.length - 1 ? '1px solid #CBD5E1' : 'none',
                        bgcolor: selected ? color : 'transparent',
                        color: selected
                          ? theme.palette.getContrastText(color)
                          : 'text.secondary',
                        '&:hover': !selected && { bgcolor: 'anMuted.main' },
                      }}
                    >
                      {status === 'Completed' && <CheckIcon sx={{ fontSize: 16 }} />}
                      {STATUS_META[status].label}
                    </Box>
                  )
                })}
              </Stack>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: 2,
              }}
            >
              <Box>
                <FieldLabel>Assigned to</FieldLabel>
                <AssignedTo
                  value={task?.assignedToTeam || task?.assignedToUser || null}
                  onChange={(e, value) =>
                    update(
                      typeof value === 'string'
                        ? { assignedToUser: null, assignedToTeam: value }
                        : { assignedToUser: value, assignedToTeam: null },
                    )
                  }
                />
              </Box>
              <Box>
                <FieldLabel>Due date</FieldLabel>
                <AnDateField
                  date={task.dueDate}
                  fullWidth
                  onChange={(value) => update({ dueDate: value })}
                />
              </Box>
              <Box>
                <FieldLabel>Priority</FieldLabel>
                <TextField
                  select
                  fullWidth
                  value={task.taskPriority ?? 'Medium'}
                  onChange={(e) => update({ taskPriority: e.target.value })}
                  SelectProps={{
                    renderValue: (value) => (
                      <Stack direction="row" alignItems="center" gap={1}>
                        <Dot color={resolveColor(theme, PRIORITY_META[value].colorKey)} />
                        {value}
                      </Stack>
                    ),
                  }}
                >
                  {PRIORITIES.map((priority) => (
                    <MenuItem key={priority} value={priority}>
                      <Stack direction="row" alignItems="center" gap={1}>
                        <Dot color={resolveColor(theme, PRIORITY_META[priority].colorKey)} />
                        {priority}
                      </Stack>
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box>
                <FieldLabel>Created by</FieldLabel>
                <Stack direction="row" alignItems="center" gap={1} sx={{ height: 40 }}>
                  <Avatar
                    src={task?.createdBy?.photograph}
                    sx={{ width: 24, height: 24, borderRadius: 1, fontSize: 11 }}
                  >
                    {task?.createdBy?.firstName?.[0]}
                    {task?.createdBy?.lastName?.[0]}
                  </Avatar>
                  <Typography variant="body2" color="text.primary">
                    {task?.createdBy?.firstName} {task?.createdBy?.lastName}
                  </Typography>
                </Stack>
              </Box>
            </Box>

            {linkedRecords.length > 0 && (
              <Box>
                <FieldLabel>Linked records</FieldLabel>
                <Stack direction="row" gap={1} flexWrap="wrap">
                  {linkedRecords.map((link) => {
                    const record = task.links[link.key]
                    return (
                      <Chip
                        key={link.key}
                        component={Link}
                        href={link.url(record)}
                        clickable
                        variant="outlined"
                        size="small"
                        label={`${link.type} · ${link.label(record) ?? ''}`}
                        deleteIcon={<OpenInNewIcon />}
                        onDelete={() => (window.location.href = link.url(record))}
                        sx={{ borderRadius: 1, textDecoration: 'none' }}
                      />
                    )
                  })}
                </Stack>
              </Box>
            )}

            <Box>
              <FieldLabel>Description</FieldLabel>
              <TipTap value={taskDescription} onChange={setTaskDescription} />
            </Box>

            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
              <Button variant="contained" onClick={() => save()} disabled={saving}>
                Save changes
              </Button>
              {!completed && (
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<CheckIcon />}
                  disabled={saving}
                  onClick={() => save({ taskStatus: 'Completed' })}
                >
                  Mark as completed
                </Button>
              )}
              {!assignedToMe && (
                <Button
                  size="small"
                  color="inherit"
                  sx={{ ml: 'auto', color: 'text.secondary' }}
                  disabled={saving}
                  onClick={() =>
                    update({
                      assignedToUser: {
                        _id: user._id,
                        firstName: user.firstName,
                        lastName: user.lastName,
                        photograph: user.photograph,
                      },
                      assignedToTeam: null,
                    })
                  }
                >
                  Assign to me
                </Button>
              )}
            </Stack>

            <Divider />

            <Box>
              <Stack direction="row" alignItems="baseline" gap={1} mb={1}>
                <Typography variant="title">Comments</Typography>
                <Typography variant="caption" color="text.secondary">
                  {task.comments?.length ?? 0}
                </Typography>
              </Stack>
              <Comments
                comments={task.comments ?? []}
                onSubmit={(comment) =>
                  new Promise((resolve, reject) => {
                    API.post('/task-comment', { taskID: task._id, comment }).then(
                      (res) => {
                        if (res.status === 200) {
                          setTask((prev) => ({
                            ...prev,
                            comments: [
                              ...(prev.comments ?? []),
                              {
                                author: {
                                  firstName: user.firstName,
                                  lastName: user.lastName,
                                  photograph: user.photograph,
                                },
                                comment,
                                createdAt: moment(),
                                _id: moment().toISOString(),
                              },
                            ],
                          }))
                          resolve()
                        } else {
                          reject()
                        }
                      },
                    )
                  })
                }
              />
            </Box>
          </Stack>
        </Stack>
      )}
    </Box>
  )
}

export default TaskPane
